import {
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  ViewChild
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { ZardTabComponent, ZardTabGroupComponent } from '@shared/components/ui/tabs/tabs.component';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { PermissionsDirective } from 'app/core/permissions/permissions.directive';
import { Subject, filter, takeUntil } from 'rxjs';
import { ITabbedPageAction } from './tabbed-page-layout.types';
import { NgClass, NgComponentOutlet } from '@angular/common';
import { IBreadCrumb } from 'app/shared/shared.types';
import { BreadcrumbService } from 'app/shared/services/breadcrumb/breadcrumb.service';
import { TranslocoPipe } from '@ngneat/transloco';
import { MatDialog } from '@angular/material/dialog';
import { PlayerCardDialogComponent } from 'app/shared/components/player-card-dialog/player-card-dialog.component';
import { BulkSelectionService } from 'app/shared/components/table/bulk-selection.service';
import { PlayerCardComponent } from 'app/shared/components/player-card/player-card.component';
import { ZardButtonComponent } from 'app/shared/components/ui/button/button.component';

@Component({
  selector: 'tabbed-page-layout',
  templateUrl: './tabbed-page-layout.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NgClass,
    MatIconModule,
    MatButtonModule,
    MatTabsModule,
    CdkScrollable,
    RouterLink,
    RouterOutlet,
    PermissionsDirective,
    ZardTabComponent,
    ZardTabGroupComponent,
    NgComponentOutlet,
    TranslocoPipe,
    PlayerCardComponent,
    ZardButtonComponent
  ],
})
export class TabbedPageLayoutComponent implements OnInit, OnDestroy {
  @ViewChild(RouterOutlet) routerOutlet: RouterOutlet;
  @ViewChild(PlayerCardComponent) playerCard: PlayerCardComponent;
  @Input() breadcrumbs: IBreadCrumb[] = [];
  @Input() title: string = '';
  @Input() actions: ITabbedPageAction[] = [];
  @Input() tabs: { label: string; route?: string; primary?: boolean; permission?: string }[] = [];
  @Input() showBulkActionButton: boolean = true;

  activeLink: string;
  bulkSelectedRows: any[] = [];
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  showPlayerInfo: boolean = false;
  player: any;
  private _popupComponent: any;
  private _popupData: any;
  alwaysVisibleActions: ITabbedPageAction[] = [];
  rowDependentActions: ITabbedPageAction[] = [];
  id: any;

  // Services
  private _dialog = inject(MatDialog);
  private bulkSelectionService = inject(BulkSelectionService);
  private breadcrumbService = inject(BreadcrumbService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  ngOnInit(): void {
    // Bulk selection subscription
    this.bulkSelectionService.config$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.bulkSelectedRows = config.rows || [];
        this._popupComponent = config.popupComponent;
        this._popupData = config.data;
      });

    // Listen for router events
    this.router.events
      .pipe(
        filter((event: any) => event?.routerEvent instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => {
        this._updateLayout();
        this.bulkSelectionService.clear();
      });

    this.router.events.subscribe(() => {
      this.updateActiveLink();
    });

    // Update active link
    this.updateActiveLink();

    // Subscribe to breadcrumbs
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((breadcrumbs) => {
        this.breadcrumbs = breadcrumbs;
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
    this.bulkSelectionService.clear();
  }



  handleBulkAction(): void {
    if (!this.bulkSelectedRows.length) return;
    const popupToOpen = this._popupComponent;
    if (!popupToOpen) return;
    const dialogData = this._popupData || { selectedRows: this.bulkSelectedRows };
    const dialogRef = this._dialog.open(popupToOpen, {
      width: '500px',
      data: dialogData
    });
    
    // Handle dialog result if component has handleBulkAction method
    dialogRef.afterClosed().subscribe((result) => {
      if (result && this.routerOutlet?.component) {
        const component = this.routerOutlet.component as any;
        if (typeof component.handleBulkAction === 'function') {
          component.handleBulkAction(result);
        }
      }
    });
  }

  updateActiveLink(): void {
    const url = this.router.url;
    const activeTab = this.tabs.find((tab) => url.includes(tab.route));
    this.activeLink = activeTab ? activeTab.route : '';
    this.showPlayerInfo = /^\/players\/\d+\/.*/.test(url);
  }



  private _updateLayout(): void {
    let currentRoute = this.route;
    while (currentRoute) {
      const data = currentRoute.snapshot.data;
      if (data['breadcrumbs']) {
        this.breadcrumbs = data['breadcrumbs'];
      }
      this.title = data['title'] ? data['title'] : '';
      this.actions = data['actions'] ? data['actions'] : [];
      this.alwaysVisibleActions = data['alwaysVisibleActions'] || [];
      this.rowDependentActions = data['rowDependentActions'] || [];
      if (typeof data['showBulkActionButton'] === 'boolean') {
        this.showBulkActionButton = data['showBulkActionButton'];
      }
      if (data['tabs']) {
        this.tabs = data['tabs'];
      }
      if (data['player']) {
        this.player = data['player'];
        this.id = this.player.id;
      }
      currentRoute = currentRoute.firstChild;
    }
  }

  navigateTo(route: string): void {
    this.router.navigate([route], { relativeTo: this.route });
  }

  getActiveTabIndex(): number {
    return this.tabs.findIndex(tab => tab.route === this.activeLink);
  }



  handleActionClick(action: ITabbedPageAction): void {
    if (action.execute) {
      const componentInstance = this.routerOutlet?.component;
      if (componentInstance) {
        action.execute(componentInstance);
      } else {
        console.error('Component instance not found in RouterOutlet');
      }
    } else if (action.route) {
      this.router.navigate([action.route], { relativeTo: this.route });
    }
  }

  openPlayerCardDialog(): void {
    this._dialog.open(PlayerCardDialogComponent, {
      height: '100vh',
      panelClass: 'bottom-dialog',
      data: {
        player: this.player
      }
    });
  }
}
